import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../rootReducer";
import { MethodStep } from "../../models/method-step";
import { deleteRecipeAction } from "../actions";
import { makeSyncUpsert, makeSyncRemove } from "../syncUpsert";
import { normalizeField } from "../../utils/textNormalize";

export const methodStepsAdapter = createEntityAdapter<MethodStep>();
type MethodStepsState = EntityState<MethodStep, string>;
const initialState: MethodStepsState = methodStepsAdapter.getInitialState();

type SaveMethodStepsPayload = {
  recipeId: string;
  methodSteps: MethodStep[];
};

function setMethodStepsForRecipe(
  state: MethodStepsState,
  recipeId: string,
  methodSteps: MethodStep[],
): void {
  const now = new Date().toISOString();

  // Build the desired set (drops blank steps, renumbers), stamping sync fields
  const entries = methodSteps
    .filter((methodStep) => methodStep.instruction?.trim())
    .map((methodStep, index) => ({
      ...methodStep,
      // Clean paste artifacts from manually-entered/edited step text on save.
      instruction: normalizeField(methodStep.instruction),
      stepNumber: index + 1,
      recipeId,
      deletedAt: undefined,
      createdAt: state.entities[methodStep.id]?.createdAt ?? methodStep.createdAt ?? now,
      updatedAt: now,
    }));

  const desiredIds = new Set(entries.map((e) => e.id));

  // Soft-delete existing live rows for this recipe no longer present (incl. blanked steps)
  const updates = Object.values(state.entities)
    .filter((i) => i?.recipeId === recipeId && !desiredIds.has(i!.id) && !i!.deletedAt)
    .map((i) => ({ id: i!.id, changes: { deletedAt: now } }));
  methodStepsAdapter.updateMany(state, updates);

  methodStepsAdapter.upsertMany(state, entries);
}

export const methodStepsSlice = createSlice({
  name: "methodSteps",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(methodStepsAdapter),
    removeManyFromSync: makeSyncRemove(methodStepsAdapter),
    addMethodSteps(
      state: MethodStepsState,
      action: PayloadAction<SaveMethodStepsPayload>,
    ): void {
      const { recipeId, methodSteps } = action.payload;
      setMethodStepsForRecipe(state, recipeId, methodSteps);
    },
    updateMethodSteps(
      state: MethodStepsState,
      action: PayloadAction<SaveMethodStepsPayload>,
    ): void {
      const { recipeId, methodSteps } = action.payload;
      setMethodStepsForRecipe(state, recipeId, methodSteps);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deleteRecipeAction, (state, action) => {
      const now = new Date().toISOString();
      const updates = Object.values(state.entities)
        .filter((i) => i?.recipeId === action.payload)
        .map((i) => ({
          id: i!.id,
          changes: { deletedAt: now },
        }));

      methodStepsAdapter.updateMany(state, updates);
    });
  },
});

const methodStepSelectors = methodStepsAdapter.getSelectors(
  (state: RootState) => state.methodSteps,
);

export const selectMethodStepById = methodStepSelectors.selectById;

// selectAll* hides soft-deleted rows; sync reads entities directly.
export const selectAllMethodSteps = createSelector(
  methodStepSelectors.selectAll,
  (methodSteps) => methodSteps.filter((ms) => !ms.deletedAt),
);

// TODO need a more efficient way to do this
export const selectMethodStepsByRecipeId = createSelector(
  [selectAllMethodSteps, (_: RootState, recipeId: string) => recipeId],
  (methodSteps, recipeId) =>
    // Sort by stepNumber so reorders (which renumber on save) actually take
    // effect — the entity adapter keeps upserted rows in their original id order.
    methodSteps
      .filter((rc) => rc.recipeId === recipeId)
      .sort((a, b) => (a.stepNumber ?? 0) - (b.stepNumber ?? 0)),
);

export const {
  addMethodSteps,
  updateMethodSteps,
  upsertManyFromSync: upsertMethodStepsFromSync,
  removeManyFromSync: removeMethodStepsFromSync,
} = methodStepsSlice.actions;

export default methodStepsSlice.reducer;
